// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import cartLineSharedStyles from "@awc/App/SharedStyles/CartLineSharedStyles";
import { requestRmaProductListStyles } from "@awc/Overrides/Widgets/RequestRma/RequestRmaProductList";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import { mergeWithCss, xScreen } from "@awc/XDL/Utilities";
import { css } from "styled-components";

requestRmaProductListStyles.productTotalPriceHeadingAndText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText, {
        extendedStyles: { text: { lineHeight: fontConfig.lineHeight.singleLinexSmall } },
    }),
};

requestRmaProductListStyles.productDescriptionStyles = {
    ...cartLineSharedStyles.productDescription,
};

requestRmaProductListStyles.productPartNumbers = {
    ...cartLineSharedStyles.productPartNumbers,
};

requestRmaProductListStyles.returnReasonSelect = {
    mergeCss: true,
    cssOverrides: {
        formField: css`
            width: auto;
            margin-top: 10px;
            ${xScreen("lte", "sm")} {
                margin-top: 0;
                width: 48%;
            }
        `,
    },
};

requestRmaProductListStyles.qtyToReturnTextField = {
    cssOverrides: {
        formField: css`
            margin: 0;
            width: 100%;
            justify-content: space-between;
            ${xScreen("lte", "sm")} {
                justify-content: flex-start;
                width: 48%;
            }
        `,
        formInputWrapper: css`
            width: 80px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

requestRmaProductListStyles.gridContainer = {
    css: css`
        margin-bottom: 25px;
    `,
};

requestRmaProductListStyles.cartLineContainerStyles = {
    container: {
        css: css`
            grid-template-columns: 2fr 5fr 1fr 1fr;
            width: 75%;
            row-gap: 0;
            ${xScreen("lte", "lg")} {
                grid-template-columns: 2fr 5fr 1fr 1fr;
            }
            ${xScreen("lte", "md")} {
                grid-template-columns: 1fr 5fr 2fr;
                row-gap: 5px;
            }
            ${xScreen("lte", "sm")} {
                grid-template-columns: 1fr 2fr 1fr;
                width: 100%;
                &:not(:last-child) {
                    margin-bottom: 0;
                }
            }
        `,
    },
    actions: {
        css: css`
            grid-column: 3 / 7;
            justify-content: end;
            ${xScreen("lte", "md")} {
                justify-content: start;
            }
        `,
    },
    image: {
        css: css`
            display: flex;
            justify-content: center;
            ${xScreen("lte", "md")} {
                grid-column: 1 / 2;
                grid-row: 1 / 5;
            }
            ${xScreen("lte", "sm")} {
                grid-column: 1 / 5;
                grid-row: 1 / 2;
            }
        `,
    },
    description: {
        css: css`
            grid-column: 2 / 3;
            ${xScreen("lte", "md")} {
                padding: 0;
            }
            ${xScreen("lte", "sm")} {
                grid-column: 2 / 5;
            }
        `,
    },
    price: {
        css: css`
            ${xScreen("lte", "sm")} {
                grid-column: 2 / 4;
                text-align: left;
            }
        `,
    },
    qty: {
        css: css`
            grid-column: 3/4;
            grid-row: 2/3;
            text-align: left;
            display: flex;

            ${xScreen("lte", "sm")} {
                grid-column: 3 / 4;
                grid-row: 4/5;
                text-align: left;
            }
        `,
    },
    total: {
        css: css`
            grid-column: 4/5;
            grid-row: 2/3;

            ${xScreen("lte", "sm")} {
                grid-column: 4/5;
                grid-row: 4 / 5;
            }
        `,
    },
    notes: {
        css: css`
            grid-column: 2/4;
            grid-row: 3/4;

            ${xScreen("lte", "sm")} {
                grid-column: 1/4;
                grid-row: 5/6;
            }
        `,
    },
};

requestRmaProductListStyles.priceHeading = {
    ...cartLineSharedStyles.extendedUnitNetPriceText?.extendedStyles?.heading,
};

requestRmaProductListStyles.priceText = {
    ...mergeWithCss(cartLineSharedStyles.extendedUnitNetPriceText?.extendedStyles?.text, {
        weight: fontConfig.weight.regular,
        lineHeight: fontConfig.lineHeight.singleLinexSmall,
        css: css`
            display: inline-flex;
            width: auto;
        `,
    }),
};

requestRmaProductListStyles.unitOfMeasure = {
    ...mergeWithCss(cartLineSharedStyles.unitOfMeasure, {
        css: css`
            display: inline-flex;
            line-height: ${fontConfig.lineHeight.xSmall};
            margin-left: 4px;
        `,
    }),
};

requestRmaProductListStyles.orderLineCardImage = {
    css: css`
        ${cartLineSharedStyles?.productImage?.linkWrappingImage?.css}
    `,
};

requestRmaProductListStyles.rowContainer = {
    css: css`
        display: flex;
        gap: 16px;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
        }
    `,
};

requestRmaProductListStyles.actionsContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-top: 16px;
        flex-grow: 1;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
            padding-top: 0;
            margin-bottom: 32px;
            justify-content: space-between;
        }
    `,
};

requestRmaProductListStyles.qtyFieldStyles = {
    qtyLabel: {
        color: "secondary",
        lineHeight: fontConfig.lineHeight.singleLinexSmall,
    },
    valueText: { lineHeight: fontConfig.lineHeight.singleLinexSmall },
};

requestRmaProductListStyles.priceWrapper = {
    css: css`
        display: flex;
    `,
};

requestRmaProductListStyles.notesStyles = {
    wrapper: {
        css: css`
            flex-direction: row;
            align-items: baseline;
            gap: 8px;
            ${xScreen("lte", "sm")} {
                margin-top: 5px;
            }
        `,
    },
    heading: {
        color: "secondary",
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.bold,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        css: css`
            text-transform: capitalize;
            &:after {
                content: ":";
            }
        `,
    },
    text: {
        css: css`
            width: auto;
        `,
    },
};
